class SearchDrawer extends window.BAO.CustomElement() {
  static get requiredElements () {
    return [
      'input',
      'products-panel',
      'pages-panel',
      'articles-panel',
      'default-content',
      'collections',
      'products-grid',
      'pages-grid',
      'articles-grid',
      'query-suggestions',
      'query-suggestions-list',
    ]
  }

  constructor () {
    super()
    this.onInputChange = this.onInputChange.bind(this)
    this.handleImmediateInput = this.handleImmediateInput.bind(this)
    this.currentRequest = null
  }

  connectedCallback () {
    super.connectedCallback()
  }

  handleImmediateInput () {
    const searchTerm = this.els.input.element.value.trim()

    if (!searchTerm) {
      this.showResults(false)
      this.resetSearchState()
      if (this.currentRequest) {
        this.currentRequest.abort()
        this.currentRequest = null
      }
    }
  }

  setupListeners () {
    super.setupListeners()

    if (this.els.input?.element) {
      this.els.input.element.addEventListener('input', this.onInputChange)
      this.els.input.element.addEventListener(
        'input',
        this.handleImmediateInput,
      )
    }

    const popularSearchButtons = this.querySelectorAll('[data-search-term]')
    popularSearchButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        const searchTerm = e.target.getAttribute('data-search-term')
        if (this.els.input?.element) {
          this.els.input.element.value = searchTerm
          this.els.input.element.focus()
          this.onInputChange()
        }
      })
    })
  }

  onInputChange () {
    const searchTerm = this.els.input.element.value.trim()
    const MIN_SEARCH_LENGTH = 2

    if (!searchTerm || searchTerm.length < MIN_SEARCH_LENGTH) {
      this.showResults(false)
      this.resetSearchState()
      return
    }

    try {
      this.getSearchResults(searchTerm)
      this.showResults(true)
    } catch (error) {
      console.error('Search failed:', error)
      this.showResults(false)
    }
  }

  async getSearchResults (searchTerm) {
    try {
      if (this.currentRequest) {
        this.currentRequest.abort()
      }

      this.currentRequest = new AbortController()

      const response = await fetch(
        `/search/suggest?q=${searchTerm}&resources[type]=product,collection,query,article,page&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,body,author&section_id=predictive-search`,
        {
          signal: this.currentRequest.signal,
        },
      )
      const text = await response.text()

      if (this.els.input.element.value.trim() !== searchTerm) {
        return
      }

      const html = document.createElement('div')
      html.innerHTML = text

      const productResults = html.querySelector('#predictive-search-products')
      const collectionResults = html.querySelector(
        '#predictive-search-collections',
      )
      const queryResults = html.querySelector('#predictive-search-queries')
      const articleResults = html.querySelector('#predictive-search-articles')
      const pageResults = html.querySelector('#predictive-search-pages')

      this.handleProductResults(productResults, searchTerm)
      this.handleCollectionResults(collectionResults, searchTerm)
      this.handleQueryResults(queryResults, searchTerm)
      this.handleArticleResults(articleResults, searchTerm)
      this.handlePageResults(pageResults, searchTerm)
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.error('Search API error:', e)
      }
    } finally {
      if (this.currentRequest && this.currentRequest.signal.aborted) {
        this.currentRequest = null
      }
    }
  }

  showResults (state) {
    if (!this.els) {
      console.error('Elements not found!')
      return
    }

    this.els['default-content'].element.setAttribute(
      'aria-hidden',
      state === true,
    )
    this.els['products-panel'].element.setAttribute(
      'aria-hidden',
      state === false,
    )
    this.els['articles-panel'].element.setAttribute(
      'aria-hidden',
      state === false,
    )
    this.els['pages-panel'].element.setAttribute(
      'aria-hidden',
      state === false,
    )
    this.els.collections.element.setAttribute('aria-hidden', state === false)
    this.els['query-suggestions'].element.setAttribute(
      'aria-hidden',
      state === false,
    )
  }

  resetSearchState () {
    if (this.els['products-grid']) {
      this.els['products-grid'].element.innerHTML = ''
    }
    if (this.els['articles-grid']) {
      this.els['articles-grid'].element.innerHTML = ''
    }
    if (this.els['pages-grid']) {
      this.els['pages-grid'].element.innerHTML = ''
    }
    if (this.els.collections) {
      this.els.collections.element.innerHTML = ''
    }
    if (this.els['query-suggestions-list']) {
      this.els['query-suggestions-list'].element.innerHTML = ''
    }

    if (this.els.collections) {
      this.els.collections.element.setAttribute('aria-hidden', true)
    }
    if (this.els['articles-panel']) {
      this.els['articles-panel'].element.setAttribute('aria-hidden', true)
    }
    if (this.els['pages-panel']) {
      this.els['pages-panel'].element.setAttribute('aria-hidden', true)
    }
    if (this.els['query-suggestions']) {
      this.els['query-suggestions'].element.setAttribute('aria-hidden', true)
    }
  }

  handleProductResults (results, searchTerm) {
    if (results) {
      this.els['products-grid'].element.innerHTML = results.innerHTML
    }
  }

  handleCollectionResults (results) {
    if (!this.els) {
      console.error('Elements not found in handleCollectionResults!')
      return
    }
    if (results && results.innerHTML.trim()) {
      this.els.collections.element.innerHTML = results.innerHTML
      this.els.collections.element.setAttribute('aria-hidden', false)
    } else {
      this.els.collections.element.setAttribute('aria-hidden', true)
    }
  }

  handleQueryResults (results) {
    if (!this.els) {
      console.error('Elements not found in handleQueryResults!')
      return
    }
    if (results && results.innerHTML.trim()) {
      this.els['query-suggestions-list'].element.innerHTML = results.innerHTML
      this.els['query-suggestions'].element.setAttribute('aria-hidden', false)
    } else {
      this.els['query-suggestions'].element.setAttribute('aria-hidden', true)
    }
  }

  handleArticleResults (results) {
    if (!this.els) {
      console.error('Elements not found in handleArticleResults!')
      return
    }
    if (results && results.innerHTML.trim()) {
      this.els['articles-grid'].element.innerHTML = results.innerHTML
      this.els['articles-panel'].element.setAttribute('aria-hidden', false)
    } else {
      this.els['articles-panel'].element.setAttribute('aria-hidden', true)
    }
  }

  handlePageResults (results) {
    if (!this.els) {
      console.error('Elements not found in handlePageResults!')
      return
    }
    if (results && results.innerHTML.trim()) {
      this.els['pages-grid'].element.innerHTML = results.innerHTML
      this.els['pages-panel'].element.setAttribute('aria-hidden', false)
    } else {
      this.els['pages-panel'].element.setAttribute('aria-hidden', true)
    }
  }
}

if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer)
}
